<template>
  <a-modal
    width="1000px"
    :visible="visible"
    title="订单退货"
    @cancel="handleCancel"
    :maskClosable="false"
    :footer="null"
  >
    <div class="refund-modal-container" v-if="orderData">
      <!-- 订单基本信息 -->
      <a-card
        :bordered="false"
        title="订单信息"
        :headStyle="{ 'background-color': '#f3f3f3', 'height': '40px', 'border-bottom': '1px solid #DDD' }"
        :bodyStyle="{ 'background-color': '#fbfbfb' }"
        style="margin-bottom: 16px"
      >
        <a-descriptions :column="2" bordered size="small">
          <a-descriptions-item label="订单编号">{{ orderData.id }}</a-descriptions-item>
          <a-descriptions-item label="订单状态">{{ orderData.orderStatusName }}</a-descriptions-item>
          <a-descriptions-item label="买家名称">{{ orderData.buyerName }}</a-descriptions-item>
          <a-descriptions-item label="下单时间">{{ orderData.createTime }}</a-descriptions-item>
          <a-descriptions-item label="实付金额">¥{{ Number(orderData.payAmount || 0).toFixed(2) }}</a-descriptions-item>
          <a-descriptions-item label="支付方式">{{ orderData.payTypeName }}</a-descriptions-item>
        </a-descriptions>
      </a-card>

      <!-- 退货商品选择 -->
      <a-card
        :bordered="false"
        title="退货商品"
        :headStyle="{ 'background-color': '#f3f3f3', 'height': '40px', 'border-bottom': '1px solid #DDD' }"
        :bodyStyle="{ 'background-color': '#fbfbfb' }"
        style="margin-bottom: 16px"
      >
        <a-table
          :columns="goodsColumns"
          :data-source="orderGoodsList"
          :pagination="false"
          rowKey="id"
          size="small"
        >
          <template slot="picture" slot-scope="text">
            <img v-if="text" :src="text" style="width: 50px; height: 50px; object-fit: cover;" alt="商品图片" />
          </template>
          <template slot="refundCount" slot-scope="text, record">
            <a-input-number
              :key="'refundCount-' + record.id"
              v-model="record.refundCount"
              :min="0"
              size="small"
              :style="{ width: '100px' }"
            />
          </template>
          <template slot="refundAmount" slot-scope="text, record">
            <a-input-number
              :key="'refundAmount-' + record.id"
              v-model="record.refundAmount"
              :min="0"
              :precision="2"
              size="small"
              :style="{ width: '120px' }"
            />
          </template>
        </a-table>
      </a-card>

      <!-- 退货信息 -->
      <a-form-model
        ref="refundForm"
        :model="refundForm"
        :rules="refundRules"
        :label-col="{ span: 4 }"
        :wrapper-col="{ span: 20 }"
      >
        <a-form-model-item label="退货原因" prop="returnReason">
          <a-select v-model="refundForm.returnReason" placeholder="请选择退货原因">
            <a-select-option value="商品质量问题">商品质量问题</a-select-option>
            <a-select-option value="商品描述不符">商品描述不符</a-select-option>
            <a-select-option value="不喜欢/不想要">不喜欢/不想要</a-select-option>
            <a-select-option value="其他原因">其他原因</a-select-option>
          </a-select>
        </a-form-model-item>
        <a-form-model-item label="备注说明" prop="remark">
          <a-textarea
            v-model="refundForm.remark"
            placeholder="请输入备注说明（选填）"
            :rows="3"
            :maxLength="200"
          />
        </a-form-model-item>
      </a-form-model>

      <!-- 退货汇总 -->
      <a-card
        :bordered="false"
        title="退货汇总"
        :headStyle="{ 'background-color': '#f3f3f3', 'height': '40px', 'border-bottom': '1px solid #DDD' }"
        :bodyStyle="{ 'background-color': '#fbfbfb' }"
        style="margin-bottom: 16px"
      >
        <a-row :gutter="16">
          <a-col :span="8">
            <a-statistic title="退货商品数量" :value="totalRefundCount" />
          </a-col>
          <a-col :span="8">
            <a-statistic title="原订单金额" prefix="¥" :precision="2" :value="orderData.payAmount || 0" />
          </a-col>
          <a-col :span="8">
            <a-statistic title="退货金额" prefix="¥" :precision="2" :value="totalRefundAmount" style="color: #ff4d4f;" />
          </a-col>
        </a-row>
      </a-card>

      <!-- 底部操作按钮 -->
      <div class="bottom-actions">
        <a-space>
          <a-button type="primary" :loading="submitLoading" @click="handleSubmit" :disabled="!canSubmit">
            <a-icon type="check-circle" />
            确认退货
          </a-button>
          <a-button @click="handleCancel">
            取消
          </a-button>
        </a-space>
      </div>
    </div>
  </a-modal>
</template>

<script>
import { getOrderDetail, createRefundOrder } from '@/api/retail/order'

export default {
  name: 'RefundModal',
  props: {
    visible: {
      type: Boolean,
      default: false
    },
    orderData: {
      type: Object,
      default: null
    }
  },
  data() {
    return {
      orderGoodsList: [],
      goodsColumns: [
        { title: '商品图片', dataIndex: 'picture', width: 80, align: 'center', scopedSlots: { customRender: 'picture' } },
        { title: '商品名称', dataIndex: 'goodsName', width: 150, align: 'center' },
        { title: '规格', dataIndex: 'skuName', width: 100, align: 'center' },
        { title: '单价', dataIndex: 'price', width: 80, align: 'center', customRender: (text) => `¥${Number(text || 0).toFixed(2)}` },
        { title: '购买数量', dataIndex: 'goodsCount', width: 80, align: 'center' },
        { title: '退货数量', dataIndex: 'refundCount', width: 120, align: 'center', scopedSlots: { customRender: 'refundCount' } },
        { title: '退货金额', dataIndex: 'refundAmount', width: 100, align: 'center', scopedSlots: { customRender: 'refundAmount' } }
      ],
      refundForm: {
        returnReason: '',
        remark: ''
      },
      refundRules: {
        returnReason: [
          { required: true, message: '请选择退货原因', trigger: 'change' }
        ]
      },
      submitLoading: false
    }
  },
  computed: {
    // 总退货数量
    totalRefundCount() {
      return this.orderGoodsList.reduce((sum, item) => sum + (item.refundCount || 0), 0)
    },
    // 总退货金额
    totalRefundAmount() {
      return this.orderGoodsList.reduce((sum, item) => sum + (item.refundAmount || 0), 0)
    },
    // 是否可以提交
    canSubmit() {
      return this.totalRefundCount > 0 && this.totalRefundAmount > 0 && this.refundForm.returnReason
    }
  },
  watch: {
    visible(newVal) {
      if (newVal && this.orderData) {
        this.loadOrderDetail()
      }
    }
  },
  methods: {
    async loadOrderDetail() {
      try {
        const response = await getOrderDetail(this.orderData.id)
        if (response.code == 20001 && response.data) {
          const orderDetail = response.data
          // 初始化商品列表
          this.orderGoodsList = (orderDetail.orderItemList || []).map(item => ({
            id: item.id,
            goodsId: item.goodsId,
            skuId: item.skuId,
            goodsName: item.goodsName,
            skuName: item.skuName,
            picture: item.picture,
            price: item.price,
            goodsCount: item.goodsCount,
            refundCount: 0,
            refundAmount: 0
          }))
        }
      } catch (error) {
        this.$message.error('获取订单详情失败')
      }
    },
    // 处理退货数量变化（保留空方法备用）
    handleRefundCountChange(record, value) {
      // v-model已经自动处理，不需要额外逻辑
    },
    // 处理退货金额变化（保留空方法备用）
    handleRefundAmountChange(record, value) {
      // v-model已经自动处理，不需要额外逻辑
    },
    // 提交退货
    async handleSubmit() {
      try {
        await this.$refs.refundForm.validate()

        if (this.totalRefundCount <= 0) {
          this.$message.warning('请至少选择一件退货商品')
          return
        }

        if (this.totalRefundAmount <= 0) {
          this.$message.warning('请输入退货金额')
          return
        }

        const refundItems = this.orderGoodsList
          .filter(item => item.refundCount > 0 && item.refundAmount > 0)
          .map(item => ({
            goodsId: item.goodsId,
            skuId: item.skuId,
            goodsName: item.goodsName,
            skuName: item.skuName,
            quantity: item.refundCount,
            price: item.price,
            refundAmount: item.refundAmount
          }))

        const refundData = {
          orderId: this.orderData.id,
          orderNo: this.orderData.orderNo,
          totalRefundAmount: this.totalRefundAmount,
          refundItems: refundItems,
          returnReason: this.refundForm.returnReason,
          remark: this.refundForm.remark
        }

        this.submitLoading = true
        const response = await createRefundOrder(refundData)
        if (response.code == 20001) {
          this.$message.success('退货申请提交成功')
          this.$emit('success')
        } else {
          this.$message.error(response.msg || '退货申请提交失败')
        }
      } catch (error) {
        if (error.errorFields) {
          return // 表单验证错误
        }
        this.$message.error('退货申请提交失败,请重试')
      } finally {
        this.submitLoading = false
      }
    },
    handleCancel() {
      this.$refs.refundForm?.resetFields()
      this.refundForm = {
        returnReason: '',
        remark: ''
      }
      this.orderGoodsList = []
      this.$emit('cancel')
    }
  }
}
</script>

<style lang="less" scoped>
.refund-modal-container {
  max-height: 70vh;
  overflow-y: auto;
}

.bottom-actions {
  display: flex;
  justify-content: flex-end;
  padding: 16px 0;
  border-top: 1px solid #e8e8e8;
}
</style>
